<template>
<div>
  <v-card>
    <!-- 頁面標題列（共用元件，融入卡片頂部） -->
    <ListPageHeader
      embedded
      title="經費預算申請清單（中心）"
      subtitle="檢視與審議各單位經費預算申請"
      icon="tabler-building-bank"
      :count="pageCount"
    >
      <template #actions>
        <v-btn
          prepend-icon="tabler-download"
          color="white"
          variant="flat"
          class="text-primary font-weight-bold"
          @click="exportData"
          :disabled="selectData.length === 0"
          :loading="viewParams.dwnLoading"
        >匯出</v-btn>
      </template>
    </ListPageHeader>

    <v-card-item>
      <div class="d-flex align-center mb-1">
        <VIcon icon="tabler-filter" size="20" class="text-primary mr-2" />
        <span class="text-subtitle-1 font-weight-medium">篩選條件</span>
      </div>
      <v-row class="pt-2 pb-5">
        <v-col cols="12" md="2">
          <v-text-field
            label="申請單號"
            v-model="filters.applyNo"
            clearable
            prepend-inner-icon="tabler-search"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-autocomplete
            :items="associations"
            label="協會"
            v-model="filters.association"
            clearable
            item-title="deptName"
            item-value="deptNo"
            no-data-text="無協會"
            prepend-inner-icon="tabler-search"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            :items="years"
            label="所屬年度"
            v-model="filters.year"
            clearable
            item-title="rocYear"
            item-value="adYear"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-autocomplete
            item-value="projectId"
            item-title="projectTitle"
            :items="requestItems"
            label="經費需求項目"
            v-model="filters.requestItem"
            clearable
            no-data-text="無經費需求項目"
            prepend-inner-icon="tabler-search"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="2">
          <AppInput
            v-model="filters.status"
            type="select"
            :remoteItem="deSelectSelect"
            :remoteItemParams="{ tabId }"
            item-title="label"
            item-value="value"
            placeholder="請輸入狀態"
            :disabled="false"
            :loading="false"
            clearable
          />
        </v-col>
      </v-row>
    </v-card-item>

    <InteractiveDataTable
        v-model:items-per-page="filters.size"
        v-model:page="filters.page"
        :items-length="pageCount"
        v-model="selectData"
        :headers="headers"
        :items="items"
        show-select
        item-value="applyNo"
        empty-text="查無符合條件的資料"
        empty-subtitle="請調整上方篩選條件後再試"
        empty-icon="tabler-search-off"
        :loading="loading"
        :items-per-page-options="[
          {value: 10, title: '10'},
          {value: 25, title: '25'},
          {value: 50, title: '50'},
          {value: 100, title: '100'},
        ]"
        :row-props="rowProps"
        :row-clickable="false"
        @row-click="markLastViewed"
      >
        <template v-slot:item.applyNo="{ item }">
          <div class="d-inline-flex align-center flex-nowrap">
            <v-btn 
            @click.stop="review(item)"
            color="primary" 
            variant="text" 
            class="pa-0 idt-link font-weight-bold"
            >
             {{item.applyNo}}
            </v-btn>
            <v-tooltip location="top" text="複製單號">
              <template #activator="{ props: tipProps }">
                <v-btn
                  v-bind="tipProps"
                  variant="text"
                  icon
                  size="x-small"
                  density="compact"
                  class="ms-1"
                  @click.stop="notifyCopyDocNo(item.applyNo)"
                >
                  <VIcon icon="tabler-copy" size="18" />
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </template>
        <template v-slot:item.dep="{ item }">
          {{item.deptName}}<br/>
          {{item.deptNo}}
        </template>
        <template v-slot:item.projectTitle="{ item }">
          <v-tooltip location="top" :disabled="!item.projectTitle || String(item.projectTitle).length < 18">
            <template #activator="{ props: tipProps }">
              <span v-bind="tipProps" class="idt-truncate text-truncate">
                {{ item.projectTitle || '-' }}
              </span>
            </template>
            <span>{{ item.projectTitle }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.applyDate="{ item }">
          <div class="d-inline-flex flex-column">
            <span class="d-inline-flex align-center text-medium-emphasis">
              <VIcon icon="tabler-calendar-event" size="16" class="mr-1" />
              {{ taiwanDateParts(item.applyDate).date || '-' }}
            </span>
            <span v-if="taiwanDateParts(item.applyDate).time" class="text-caption text-disabled ms-5">
              {{ taiwanDateParts(item.applyDate).time }}
            </span>
          </div>
        </template>
        <template v-slot:item.status="{ item }">
            <Status 
            :status="item.status" 
            :is-red="['Rejected', 'Revoke', 'Revoked', '駁回', '收回', '撤回']" 
            :is-green="['MApproved', 'FPassed', 'FApproved', 'SendAfterCorrect', '初審通過', '核定通過', '調整後送出', '核銷通過', '核銷核定', '財務核准', '提交會議審議通過']"
            :is-warning="['Draft', '草稿']"
            :is-info="[]"
            :is-purple="['ReturnedForRevision', 'MReturnedForRevision', 'FReturnedForRevision', '回覆修正', '競技回覆修正', '財務退回修正', '承辦回覆修正', '財務回覆修正', 'CorrectAfterApprove', '核定後修正']"
            :is-teal="['Reviewing', '財務複審中', '競技核准']"
            :is-indigo="['Resubmitted', 'MPassed', '承辦審議中']"
            :is-primary="['Submitted', '審核中', '經費核定', '審議通過', '核定後調整', '承辦初審中', '經費核定中', '已送出', '重新送出', '中心初審通過']" 
            :is-secondary="['Closed', '結案', '備查']" 
            :shared-table-id="tabId" 
            btn-class="x-large" 
            font-class="18px" 
            :status-config="getStatusData" 
          />
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="item.status=='MPassed'"
            @click.stop="review(item)"
            size="default"
            color="primary"
            prepend-icon="tabler-gavel"
            variant="tonal"
            class="idt-action-btn"
          >審議</v-btn>
          <span v-else class="text-disabled text-caption">—</span>
        </template>
      </InteractiveDataTable>
  </v-card>
</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, reactive } from "vue";
import { useLastViewedRow } from '@/composables/useLastViewedRow'
import { copyPlainText } from '@/utils/deUtil'
import Status from '../tnm/StatusComponenet.vue'
definePage({
  meta: {
    action: 'manage', //固定為 manage
    subject: 'EXPENSEREQUESTCENTERLIST', //系統代碼(系統授權設定中的系統代碼)
  },
})
const router = useRouter()
const fileUrl = useStorage('drFsUrl').value

const viewParams = reactive({
  dwnLoading: false
});

const filters=reactive({
  name: "",
  association: null,
  year: null,
  requestItem: null,
  status: null,
  applyNo: "",
  size: 100,
  page: 1,
})

const FILTER_STATE_KEY = 'erm_expense_request_center_list_filter_state'
const restoreListFilters = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STATE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw) as Record<string, unknown>
    for (const k of Object.keys(filters) as (keyof typeof filters)[]) {
      if (k in saved) (filters as Record<string, unknown>)[k] = saved[k] as never
    }
  } catch { /* ignore */ }
}
const saveListFilters = () => {
  try {
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify({ ...filters }))
  } catch { /* ignore */ }
}
restoreListFilters()

const pageCount=ref(0)
const loading=ref(false)
const associations=reactive([])
const requestItems=reactive([])

const selectData =ref([])
const { setLastViewed: setLastClickedId, rowProps } = useLastViewedRow('erm_expense_request_center_list_last_clicked')

const markLastViewed = (item: any) => {
  setLastClickedId(item?.applyNo)
}

// 拆解民國年日期字串為「日期」與「時間」兩部分
const taiwanDateParts = (raw: string) => {
  const [date = '', time = ''] = convertToTaiwanYear(raw || '', '-').split(' ')

  return { date, time }
}


const getJ = async()=>{
  const { data: jwt, execute: getJwt } = await useApi<any>(createUrl('/tca/file/get', {
    query: {}
  })) 
  return jwt
}
const exportData = async(item: any) => {
  viewParams.dwnLoading = true;
  const apiUrl='/tca/expenserequestlist/exportData'
  await $api(apiUrl, {
    method: 'POST',
    body: Array.from(selectData.value)
  }).then(async(resp) => {
    viewParams.dwnLoading = false;
    if (resp.statusCode == 200) {
      const jwt = await getJ();  // 获取新的 JWT
      window.open(fileUrl+'/api/v2/files/nstc/' +resp.data+'?drJwt='+jwt.value, '_blank');
    } else  {
      popSwal('error','匯出失敗,錯誤訊息'+resp.data);
    }
  })
};
const headers=reactive([
  { title: "申請單號", key: "applyNo", icon: "tabler-file-text" },
  { title: "單位", key: "dep", icon: "tabler-building" },
  { title: "項目名稱", key: "projectTitle", icon: "tabler-clipboard-text" },
  { title: "申請日期", key: "applyDate", icon: "tabler-calendar-event" },
  { title: "狀態", key: "status", icon: "tabler-flag" },
  { title: "操作", key: "actions", sortable: false, icon: "tabler-settings" },
])
const items=reactive<any[]>([])

const popSwal = (icon: any,title: any) => {
$swal({
  position: 'top-end',
  icon: icon,
  title: title,
  showConfirmButton: false,
  timer: 3000,
  customClass: {
    confirmButton: 'btn btn-primary',
  },
  buttonsStyling: false,
});
}
const isDev = import.meta.env.MODE === 'development'
const windowOrigin = window.location.origin
// 跳轉業面
const review = async (item: any) => {
  setLastClickedId(item.applyNo)
  await nextTick()
  if(item.gold){
    router.push({ path: '/tca/erm/GoldPlanApply', query: { applyNo: item.applyNo } })
  }else{
    router.push({ path: '/tca/erm/ExpenseRequest', query: { applyNo: item.applyNo } })
  }
}

const notifyCopyDocNo = async (text: string | null | undefined) => {
  const ok = await copyPlainText(text)
  if (ok) {
    $swal({
      icon: 'success',
      title: '已複製到剪貼簿',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 1500,
    })
  } else {
    $swal({
      icon: 'error',
      title: '複製失敗',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 2000,
    })
  }
}

const copyApply = async(item: any) => {
  await $api('/tca/expenserequestlist/copyApply', { 
    method: 'POST', 
    query: {
      applyNo: item.applyNo 
    }
  }).then((resp: any) => {
    if (resp.statusCode == 200) {
      popSwal('success', '複製成功');
      fetchData();
    } else {
      popSwal('error', '複製失敗,錯誤訊息' + resp.data);
    }
  })
}

// 所屬年度API
const { data: yearList, execute: fetchFilterYearList } = await useApi<any>(createUrl('/tca/expenserequestprojectlist/getYear', { query: {} }))

const years = computed(() => yearList.value?.data ? yearList.value.data : []);

// select 項目名稱
const getProjectList = async () => {
  let annual = filters.year?filters.year:null
  await $api('/tca/expenserequestprojectlist/getAllProjectList', {
    method: 'POST',
    body: {
      deptNo: null,
      isGold: null,
      annual, 
    }
  }).then((response: any) => {
    requestItems.splice(0, requestItems.length, ...response.data)
  }).catch((error) => {
    console.error('取得項目下拉選單失敗:', error);
  });
};

// 
const fetchData = async () => {
  loading.value = true
  let applyTitle = filters.name?filters.name:null
  let projectId = filters.requestItem?filters.requestItem:null
  let status = filters.status?filters.status:null
  let deptNo = filters.association?filters.association:null
  let page = filters.page -1
  let size = filters.size
  try {
    const response: any = await $api('/tca/expenserequestlist/getList', {
      method: 'POST',
      body: {
        applyTitle,
        deptNo,
        projectId,
        status,
        applyNo: filters.applyNo ? filters.applyNo : null,
        page,
        size,
      },
    });

    if (response.data) {
      pageCount.value = response.data.totalElements
      items.splice(0, items.length, ...response.data.requestList)
      associations.splice(0, associations.length, ...response.data.deptList)
      // 目前頁碼超出實際總頁數時（例如篩選後筆數變少），自動回到最後有效頁重抓
      const maxPage = Math.max(1, Math.ceil((pageCount.value || 0) / filters.size))
      if (filters.page > maxPage) {
        filters.page = maxPage
        return
      }
    } else {
      items.splice(0, items.length)
    }
  } catch (error: any) {
    console.error('Error during fetch:', error);
  }finally{
    loading.value = false
  }
}

const tabId = ref('ExpenseRequest')
const { data: getStatusData, execute: fetchStatus } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
  query: {
    tabId: tabId.value
  },
}))

watch(() => filters.year, () => {
  getProjectList()
})
watch(filters, () => {
  fetchData()
})
onMounted(() => {
  fetchData()
  getProjectList()
})

onUnmounted(() => saveListFilters())
</script>
